/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps, Ref } from 'preact';
import {
  AriaAttributesSignalExcluded,
  HTMLAttributesSignalExcluded
} from '#utils/UNSAFE_attributeUtils';
import { BundleType } from '#resources/nls/bundle';
import { classNames } from '#utils/UNSAFE_classNames';
import { DateSegment } from './DateSegment';
import { DateSegmentState } from './useDateField';
import { Flex } from '#UNSAFE_Flex';
import { HiddenAccessible } from '#UNSAFE_HiddenAccessible';
import { InputDateMask } from './InputDateMask';
import { LiteralSegment } from './LiteralSegment';
import { LiveRegion } from '#UNSAFE_LiveRegion';
import { merge } from '#utils/UNSAFE_stringUtils';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { useDateField } from './useDateField';
import { useDateFieldHandlers } from './useDateFieldHandlers';
import { useFormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { useUser } from '#hooks/UNSAFE_useUser';
import { TextFieldInputVariantOptions } from '#UNSAFE_TextField/themes/TextFieldInputStyles.css';
import { TextFieldInputRedwoodTheme } from '#UNSAFE_TextField/themes/redwood/TextFieldInputTheme';
import type { DatePlaceholders, EditableDateSegment } from './types';
import { formatFullCalendarDate } from '#utils/UNSAFE_calendarDateUtils';

type InputDateMaskProps = ComponentProps<typeof InputDateMask>;

type AriaProps = Pick<
  AriaAttributesSignalExcluded,
  'aria-describedby' | 'aria-invalid' | 'aria-label' | 'aria-labelledby'
>;

type HTMLElementProps = Pick<HTMLAttributesSignalExcluded<HTMLElement>, 'onBlur' | 'onFocus'>;

export type Props = AriaProps &
  HTMLElementProps & {
    /**
     * A base id prefix.
     */
    baseId: string;

    /**
     * A ref to the outer div element.
     */
    fieldRef?: Ref<HTMLDivElement>;

    /**
     * Specifies the smallest date unit that is displayed by the component.
     * If set to month, only month and year are shown.
     * If set to day then month, day, and year are shown.
     */
    granularity?: InputDateMaskProps['granularity'];

    /**
     * True if the component has an inside label.
     */
    hasInsideLabel?: boolean;

    /**
     * Specifies for accessibility purposes whether a value is required.
     */
    isRequired?: InputDateMaskProps['isRequired'];

    /**
     * The placeholder masks used by the date field.
     */
    masks: DatePlaceholders;

    /**
     * Specifies how to align text within the field.
     */
    textAlign?: InputDateMaskProps['textAlign'];

    /**
     * The current display value of the component.
     */
    value?: InputDateMaskProps['value'];

    /**
     * The style variant of the component.
     */
    variant?: InputDateMaskProps['variant'];

    /**
     * Callback invoked each time the user changes the value of a segment For example, if the user
     * types '12' into the month segment, this callback will be called twice to change the month
     * first to '1' and then '12'.
     */
    onInput: InputDateMaskProps['onInput'];

    /**
     * Callback invoked when the user commits the entered value by blurring or hitting the enter key.
     */
    onCommit: InputDateMaskProps['onCommit'];
  };

const findSegment = (segments: DateSegmentState[], type: EditableDateSegment): DateSegmentState => {
  return segments.find((segment) => segment.type === type)!;
};

/**
 * Container for Segment children that represent different parts of a date with no time.
 */
const DateField = ({
  'aria-describedby': describedBy,
  'aria-invalid': ariaInvalid,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  baseId,
  fieldRef,
  granularity = 'day',
  hasInsideLabel,
  isRequired,
  masks: dateMasks,
  onBlur,
  onCommit,
  onFocus,
  onInput,
  textAlign,
  value,
  variant = 'default'
}: Props) => {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const announceDateCleared = translations.inputDateMask_date_cleared();
  const labels = {
    month: translations.formControl_month(),
    day: translations.formControl_day(),
    year: translations.formControl_year()
  };

  const { direction, locale } = useUser();

  const { isDisabled, isFocused, isInputFocused } = useFormFieldContext();
  const hasValue = value !== undefined;

  // DateField is similar to TextFieldInput for styling. The main difference
  // is TextFieldInput renders a textarea or input and DateField renders a div, so the positioning with
  // padding is different. DateField and TextFieldInput are both the mainContent
  // of a TextField component.
  const { classes } = useComponentTheme<TextFieldInputVariantOptions>(TextFieldInputRedwoodTheme, {
    type: 'notPassword',
    styleVariant: variant,
    textarea: 'notTextArea',
    input: 'notInput',
    div: 'isDiv',
    prefix: 'noPrefix',
    suffix: 'noSuffix',
    startContent: 'noStartContent',
    endContent: 'noEndContent',
    insideLabel: hasInsideLabel ? 'hasInsideLabel' : 'noInsideLabel',
    value: hasValue ? 'hasValue' : 'noValue',
    focused: isFocused ? 'isFocused' : 'notFocused',
    disabled: isDisabled ? 'isDisabled' : 'notDisabled'
  });

  const compClasses = classNames([classes]);

  const { state, dispatch, dateResetRef } = useDateField({
    ariaLabels: labels,
    dateMasks,
    granularity,
    locale,
    value
  });
  const { isDateSelected, isPartialDate, segments: editableSegments } = state;
  const yearSegment = findSegment(editableSegments, 'year');
  const monthSegment = findSegment(editableSegments, 'month');
  const daySegment = granularity === 'day' ? findSegment(editableSegments, 'day') : undefined;
  const segmentsMap = {
    year: yearSegment,
    month: monthSegment,
    day: daySegment
  };

  const { dateClearedRef, dateFieldHandlers, segmentHandlers, tabIndex } = useDateFieldHandlers({
    dateResetRef,
    direction,
    dispatch,
    isDisabled,
    isFocused,
    isInputFocused,
    onCommit,
    onInput,
    state
  });

  // Like other form components, don't show placeholders for inside labels unless focused.
  // This flag causes the segment placeholders to be hidden.
  const isHidden = value === undefined && hasInsideLabel && !isFocused;

  // If the date is complete, create a hidden accessible region with the full formatted date.
  const formattedDateStr = !state.isCompleteDate
    ? ''
    : formatFullCalendarDate(locale, state.currentDate!, granularity);

  const formattedDateAriaId = `${baseId}-date`;
  const formattedDateAria = state.isCompleteDate ? (
    <HiddenAccessible id={formattedDateAriaId}>{formattedDateStr}</HiddenAccessible>
  ) : undefined;
  const ariaDescribedBy = state.isCompleteDate
    ? merge([describedBy, formattedDateAriaId])
    : describedBy;

  // Add an announcement to the live region if the date was cleared.
  // Note: we deliberately do not reset dateClearedRef because there are multiple
  // rerenders after clearing the date and we want to make sure the announcement
  // has time to be heard.
  const announceText = dateClearedRef.current ? announceDateCleared : '';

  return (
    <div
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      class={compClasses}
      onfocusin={onFocus}
      onfocusout={onBlur}
      role="group"
      ref={fieldRef}
      tabIndex={tabIndex}
      {...dateFieldHandlers}>
      <Flex direction="row" wrap="nowrap" justify={textAlign}>
        {dateMasks.map(({ type, value: placeholder }) => {
          const segmentProps = type !== 'literal' ? segmentsMap[type] : {};
          if (type === 'literal') {
            return (
              <LiteralSegment
                isHidden={isHidden}
                isHighlighted={isDateSelected}
                isPlaceholder={!isPartialDate}
                text={placeholder}></LiteralSegment>
            );
          } else {
            return (
              <DateSegment
                isDisabled={isDisabled}
                isInvalid={!!ariaInvalid}
                isHidden={isHidden}
                isHighlighted={isDateSelected}
                isRequired={isRequired}
                placeholder={placeholder}
                type={type}
                {...segmentHandlers}
                {...segmentProps}></DateSegment>
            );
          }
        })}
      </Flex>
      {formattedDateAria}
      <LiveRegion>{announceText}</LiveRegion>
    </div>
  );
};

export { DateField, findSegment };
